import { reactive } from 'vue'

import type {
  SolarLunarData,
  WeatherCondition,
  WeatherForecastDaily,
  WeatherForecastHourly,
} from '@onxmaps/supergraph/src/generated/types'
import type Geographic from '@/lib/geographic'

import {
  getAllWeatherDataQuery,
  getLunarForecastQuery,
} from '@/lib/weather-queries'
import { getItem } from '@/plugins/storage'

import {
  convertCelsiusToFahrenheit,
  convertCmToIn,
  convertMmToIn,
  convertMmToCm,
  convertMbarToInHg,
  convertTimezone,
  convertMeterPerSecondToMph,
  convertMeterPerSecondToKph,
} from '@/lib/weather'
import {
  HEIGHT_CM_FIELDS,
  HEIGHT_MM_FIELDS,
  PRESSURE_FIELDS,
  SOLARLUNAR_FIELDS,
  TEMPERATURE_FIELDS,
  TIME_FIELDS,
  UNITS,
  VELOCITY_FIELDS,
} from '@/services/weather-service-constants'

export type WeatherDataItem = Record<string, unknown>

export type WeatherDataBundle = {
  hasErrors: boolean
  historicalConditions: WeatherDataItem[]
  currentConditions: WeatherDataItem[]
  dailyForecast: WeatherDataItem[]
  hourlyForecast: WeatherDataItem[]
  solarLunarForecast: WeatherDataItem[]
}

type WeatherQueryResponse = {
  errors?: { message: string }[]
  data: {
    historicalConditions: WeatherCondition
    currentConditions: WeatherCondition
    dailyForecast: WeatherForecastDaily
    hourlyForecast: WeatherForecastHourly
    solarLunar: SolarLunarData
  } | null
}

type LunarQueryResponse = {
  data: {
    solarLunarData: SolarLunarData
  }
}

/**
 * Current state of this service
 */
export const observableState = reactive({
  isRequestingData: true as boolean,
  hasErrors: false as boolean,
  historicalConditions: [] as WeatherDataItem[],
  dailyForecast: [] as WeatherDataItem[],
  hourlyForecast: [] as WeatherDataItem[],
  solarLunarForecast: [] as WeatherDataItem[],
  lunarPeriods: [] as WeatherDataItem[],
  currentConditions: [] as WeatherDataItem[],
})

// Getters
/**
 * *(AI-generated)*
 * Returns whether the service has encountered errors during the last data request.
 */
export const getHasErrors = () => observableState.hasErrors
/**
 * *(AI-generated)*
 * Returns whether the service is currently requesting weather data.
 */
export const getIsRequestingData = () => observableState.isRequestingData
/**
 * *(AI-generated)*
 * Returns the historical weather conditions data.
 */
export const getHistoricalConditions = () =>
  observableState.historicalConditions
/**
 * *(AI-generated)*
 * Returns the current weather conditions data.
 */
export const getCurrentConditions = () => observableState.currentConditions
/**
 * *(AI-generated)*
 * Returns the daily forecast data.
 */
export const getDailyForecast = () => observableState.dailyForecast
/**
 * *(AI-generated)*
 * Returns the hourly forecast data.
 */
export const getHourlyForecast = () => observableState.hourlyForecast
/**
 * *(AI-generated)*
 * Returns the solar/lunar forecast data.
 */
export const getSolarLunarForecast = () => observableState.solarLunarForecast
/**
 * *(AI-generated)*
 * Returns the lunar periods data.
 */
export const getLunarPeriods = () => observableState.lunarPeriods

// Setters
/**
 * Determines if the service is currently requesting weather data
 * @param isRequestingData - whether the service is currently requesting weather data
 */
const setIsRequestingData = (isRequestingData: boolean): void => {
  observableState.isRequestingData = isRequestingData
}

const setHasErrors = (hasErrors: boolean): void => {
  observableState.hasErrors = hasErrors
}
/**
 * Sets the historical conditions in the service state
 * @param data - the response data from the weather API
 */
const setHistoricalConditions = (data: WeatherDataItem[]): void => {
  observableState.historicalConditions = data
}
/**
 * Sets the current conditions in the service state
 * @param data - the response data from the weather API
 */
const setCurrentConditions = (data: WeatherDataItem[]): void => {
  observableState.currentConditions = data
}
/**
 * Sets the daily forecast in the service state
 * @param data - the response data from the weather API
 */
const setDailyForecast = (data: WeatherDataItem[]): void => {
  observableState.dailyForecast = data
}
/**
 * Sets the hourly forecast in the service state
 * @param data - the response data from the weather API
 */
const setHourlyForecast = (data: WeatherDataItem[]): void => {
  observableState.hourlyForecast = data
}
/**
 * Sets the solar lunar forecast in the service state
 * @param data - the response data from the weather API
 */
const setSolarLunarForecast = (data: WeatherDataItem[]): void => {
  observableState.solarLunarForecast = data
}

/**
 * Sets the lunar periods in the service state
 * @param data - the response data from the weather API
 */
const setLunarPeriods = (data: WeatherDataItem[]): void => {
  observableState.lunarPeriods = data
}

/**
 * Fetches and converts weather data for a coordinate without touching any
 * shared module state. Safe to call from contexts that must not mutate the
 * weather UI singleton (e.g. MCP tools, concurrent callers).
 * @param coord - the Geographic coordinates to fetch weather for
 */
export const fetchWeatherDataForCoord = async (
  coord: Geographic
): Promise<WeatherDataBundle> => {
  const rawWeatherData = (await getAllWeatherDataQuery(
    coord
  )) as WeatherQueryResponse

  if (rawWeatherData.errors?.length && rawWeatherData.data === null) {
    return {
      hasErrors: true,
      historicalConditions: [],
      currentConditions: [],
      dailyForecast: [],
      hourlyForecast: [],
      solarLunarForecast: [],
    }
  }

  return {
    hasErrors: false,
    historicalConditions: convertWeatherData(
      (rawWeatherData.data?.historicalConditions?.periods ??
        []) as WeatherDataItem[],
      rawWeatherData.data?.historicalConditions?.timezone
    ),
    currentConditions: convertWeatherData(
      (rawWeatherData.data?.currentConditions?.periods ??
        []) as WeatherDataItem[],
      rawWeatherData.data?.currentConditions?.timezone
    ),
    dailyForecast: convertWeatherData(
      (rawWeatherData.data?.dailyForecast?.days ?? []) as WeatherDataItem[],
      rawWeatherData.data?.dailyForecast?.timezone
    ),
    hourlyForecast: convertWeatherData(
      (rawWeatherData.data?.hourlyForecast?.hours ?? []) as WeatherDataItem[],
      rawWeatherData.data?.hourlyForecast?.timezone
    ),
    solarLunarForecast: convertWeatherData(
      (rawWeatherData.data?.solarLunar?.periods ?? []) as WeatherDataItem[],
      rawWeatherData.data?.solarLunar?.timezone ?? undefined
    ),
  }
}

/**
 * Requests all weather data from the weather API
 * @param coord - the current Geographic coordinates of the map's camera
 */
export const getWeatherData = async (coord: Geographic): Promise<void> => {
  setIsRequestingData(true)

  const rawWeatherData = (await getAllWeatherDataQuery(
    coord
  )) as WeatherQueryResponse

  if (rawWeatherData.errors?.length && rawWeatherData.data === null) {
    setHasErrors(true)
  } else {
    setHasErrors(false)
    setHistoricalConditions(
      convertWeatherData(
        (rawWeatherData.data?.historicalConditions?.periods ??
          []) as WeatherDataItem[],
        rawWeatherData.data?.historicalConditions?.timezone
      )
    )
    setCurrentConditions(
      convertWeatherData(
        (rawWeatherData.data?.currentConditions?.periods ??
          []) as WeatherDataItem[],
        rawWeatherData.data?.currentConditions?.timezone
      )
    )
    setDailyForecast(
      convertWeatherData(
        (rawWeatherData.data?.dailyForecast?.days ?? []) as WeatherDataItem[],
        rawWeatherData.data?.dailyForecast?.timezone
      )
    )
    setHourlyForecast(
      convertWeatherData(
        (rawWeatherData.data?.hourlyForecast?.hours ?? []) as WeatherDataItem[],
        rawWeatherData.data?.hourlyForecast?.timezone
      )
    )
    setSolarLunarForecast(
      convertWeatherData(
        (rawWeatherData.data?.solarLunar?.periods ?? []) as WeatherDataItem[],
        rawWeatherData.data?.solarLunar?.timezone ?? undefined
      )
    )
  }
  setIsRequestingData(false)
}

/**
 * *(AI-generated)*
 * Fetches lunar period data for the given coordinate and updates the service state.
 */
export const getLunarPeriodData = async (coord: Geographic): Promise<void> => {
  const response = (await getLunarForecastQuery(coord)) as LunarQueryResponse
  setLunarPeriods(
    convertWeatherData(
      (response.data.solarLunarData.periods ?? []) as WeatherDataItem[]
    )
  )
}

/**
 * Converts data between metric/imperial and various timezones
 * @param dataset - the data to convert
 * @param timezone - the timezone to convert times to
 * @returns the converted data
 */
export const convertWeatherData = (
  dataset: WeatherDataItem[],
  timezone?: string
): WeatherDataItem[] => {
  const shouldConvertToImperial = getItem('unitType') === 'imperial'

  const convertedData = dataset.map((item) => {
    const mappedItem: WeatherDataItem = { ...item }
    mappedItem.timezone = timezone

    // add the units and return early if we don't need to convert the values
    if (!shouldConvertToImperial) {
      mappedItem.units = UNITS.METRIC

      for (const key of Object.keys(mappedItem)) {
        if (VELOCITY_FIELDS.includes(key)) {
          mappedItem[key] = convertMeterPerSecondToKph(
            mappedItem[key] as number
          )
        }

        if (HEIGHT_MM_FIELDS.includes(key)) {
          mappedItem[key] = convertMmToCm(mappedItem[key] as number)
        }

        if (TIME_FIELDS.includes(key)) {
          mappedItem[key] = convertTimezone(mappedItem[key] as string, timezone)
        }

        // solar lunar has child fields that need to be converted
        if (SOLARLUNAR_FIELDS.includes(key)) {
          convertSolarLunarFields(mappedItem, key, timezone)
        }
      }

      return mappedItem
    }

    for (const key of Object.keys(mappedItem)) {
      if (TEMPERATURE_FIELDS.includes(key)) {
        mappedItem[key] = convertCelsiusToFahrenheit(mappedItem[key] as number)
      }

      if (VELOCITY_FIELDS.includes(key)) {
        mappedItem[key] = convertMeterPerSecondToMph(mappedItem[key] as number)
      }

      if (PRESSURE_FIELDS.includes(key)) {
        mappedItem[key] = convertMbarToInHg(mappedItem[key] as number)
      }

      if (HEIGHT_CM_FIELDS.includes(key)) {
        mappedItem[key] = convertCmToIn(mappedItem[key] as number)
      }

      if (HEIGHT_MM_FIELDS.includes(key)) {
        mappedItem[key] = convertMmToIn(mappedItem[key] as number)
      }

      if (TIME_FIELDS.includes(key)) {
        mappedItem[key] = convertTimezone(mappedItem[key] as string, timezone)
      }

      // solar lunar has child fields that need to be converted
      if (SOLARLUNAR_FIELDS.includes(key)) {
        convertSolarLunarFields(mappedItem, key, timezone)
      }
    }

    mappedItem.units = UNITS.IMPERIAL

    return mappedItem
  })

  return convertedData
}

/**
 * Converts solar lunar fields to the proper timezone
 * @param mappedItem - item with solar lunar fields
 * @param key - object key
 * @param timezone - timezone to convert to
 */
const convertSolarLunarFields = (
  mappedItem: WeatherDataItem,
  key: string,
  timezone?: string
): void => {
  const childMappedItem: WeatherDataItem = {
    ...(mappedItem[key] as WeatherDataItem),
  }

  for (const childKey of Object.keys(childMappedItem)) {
    if (TIME_FIELDS.includes(childKey)) {
      childMappedItem[childKey] = convertTimezone(
        childMappedItem[childKey] as string,
        timezone
      )

      mappedItem[key] = childMappedItem
    }

    // convert solar lunar child fields on daily and hourly forecasts
    if (SOLARLUNAR_FIELDS.includes(childKey)) {
      const secondChildMappedItem: WeatherDataItem = {
        ...(childMappedItem[childKey] as WeatherDataItem),
      }

      for (const secondChildKey of Object.keys(secondChildMappedItem)) {
        if (TIME_FIELDS.includes(secondChildKey)) {
          secondChildMappedItem[secondChildKey] = convertTimezone(
            secondChildMappedItem[secondChildKey] as string,
            timezone
          )

          childMappedItem[childKey] = secondChildMappedItem
        }
      }

      mappedItem[key] = childMappedItem
    }
  }
}
